import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const unreadCount = notifications?.filter((n) => !n?.is_read)?.length || 0;

  const fetchNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('notifications')
        ?.select('*')
        ?.eq('user_id', user?.id)
        ?.order('created_at', { ascending: false })
        ?.limit(50);
      if (!error) setNotifications(data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }

    fetchNotifications();

    // Real-time subscription for this user's notifications
    const channel = supabase
      ?.channel(`notifications:${user?.id}`)
      ?.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          setNotifications((prev) => [payload?.new, ...prev]);
        }
      )
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user?.id}`,
        },
        (payload) => {
          setNotifications((prev) =>
            prev?.map((n) => (n?.id === payload?.new?.id ? payload?.new : n))
          );
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [user?.id, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('id', notificationId)
        ?.eq('user_id', user?.id);
      if (!error) {
        setNotifications((prev) =>
          prev?.map((n) => (n?.id === notificationId ? { ...n, is_read: true } : n))
        );
      }
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const markAllAsRead = async () => {
    if (!user?.id) return;
    try {
      const { error } = await supabase
        ?.from('notifications')
        ?.update({ is_read: true })
        ?.eq('user_id', user?.id)
        ?.eq('is_read', false);
      if (!error) {
        setNotifications((prev) => prev?.map((n) => ({ ...n, is_read: true })));
      }
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Helper: send notification to a specific user
  const sendNotification = async ({ userId, title, message, type = 'info', relatedId, relatedTable }) => {
    try {
      const { error } = await supabase?.from('notifications')?.insert({
        user_id: userId,
        title,
        message,
        type,
        related_id: relatedId || null,
        related_table: relatedTable || null,
        is_read: false,
      });
      if (error) console.error('Failed to send notification:', error);
    } catch (err) {
      console.error('Failed to send notification:', err);
    }
  };

  // Helper: notify all admins
  const notifyAdmins = async ({ title, message, type = 'info', relatedId, relatedTable }) => {
    try {
      const { data: admins, error } = await supabase
        ?.from('user_profiles')
        ?.select('id')
        ?.eq('role', 'admin');
      if (error || !admins?.length) return;

      const inserts = admins?.map((admin) => ({
        user_id: admin?.id,
        title,
        message,
        type,
        related_id: relatedId || null,
        related_table: relatedTable || null,
        is_read: false,
      }));

      await supabase?.from('notifications')?.insert(inserts);
    } catch (err) {
      console.error('Failed to notify admins:', err);
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    sendNotification,
    notifyAdmins,
    fetchNotifications,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
